import React, { useEffect, useRef, useState } from 'react';

/**
 * Asks for the export purpose (recorded with the export for audit).
 * onConfirm(purpose) resolves to an error message string, or null on success.
 */
// Fallbacks only; the real limits come from the dashboard response.
const ExportDialog = ({ sectionLabel, onCancel, onConfirm, minChars = 3, maxChars = 200 }) => {
  const [purpose, setPurpose] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const dialogRef = useRef(null);
  const previousFocus = useRef(null);

  useEffect(() => {
    previousFocus.current = document.activeElement;
    inputRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Tab' && dialogRef.current) {
        // Keep focus inside the dialog.
        const focusables = dialogRef.current.querySelectorAll('textarea, button:not([disabled])');
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previousFocus.current?.focus?.();
    };
  }, [onCancel]);

  const submit = async (e) => {
    e.preventDefault();
    const clean = purpose.trim();
    if (clean.length < minChars) {
      setError(
        `Please tell us briefly why you need this export (at least ${minChars} characters).`
      );
      return;
    }
    setBusy(true);
    setError('');
    const message = await onConfirm(clean);
    setBusy(false);
    if (message) setError(message);
  };

  return (
    <div className="analytics-modal-backdrop" onMouseDown={onCancel}>
      <div
        ref={dialogRef}
        className="analytics-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="analytics-export-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id="analytics-export-title">Export {sectionLabel}</h2>
        <p>
          The export contains summary figures only (no contact details). It is recorded in the
          workspace activity log together with your reason.
        </p>
        <form onSubmit={submit}>
          <label htmlFor="analytics-export-purpose">Reason for export</label>
          <textarea
            id="analytics-export-purpose"
            ref={inputRef}
            rows={3}
            maxLength={maxChars}
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="e.g. Monthly management report"
            aria-describedby={error ? 'analytics-export-error' : undefined}
          />
          {error && (
            <div id="analytics-export-error" className="analytics-form-error" role="alert">
              {error}
            </div>
          )}
          <div className="analytics-modal-actions">
            <button type="button" className="analytics-btn" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="analytics-btn primary" disabled={busy}>
              {busy ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Exporting...
                </>
              ) : (
                'Download CSV'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExportDialog;
